import React, { useEffect, useMemo } from "react";

import
  {
    CircularProgress,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar
  } from "@mui/material";
import { SelectDateRange, SelectOption } from "components/forms";
import { DealTableRow, QueryManager } from "components/reports";
import { ReportTableToolbar } from "components/reports/ReportMeasuresTable/ReportTableToolbar";
import { useCharacteristics, useDidUpdate, useQueryHook } from "hooks";
import { useAccountSelector, useRefetchReportsSelect, useReportsActions, useReportsQueriesSelector, useReportsTableSelector } from "store";
import { DateRangeValue, ReportTableData } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";

import "./Comparison.styles.scss";

interface TableColProps {
  tableData: ReportTableData | null;
  dateRange: DateRangeValue;
  hideTitle?: boolean;
  isPrimary?: boolean;
  onDateRangeChange: (value: DateRangeValue, use_accounting_date?: boolean) => void;
}

const TableCol: React.FC<TableColProps> = ({ tableData, dateRange, hideTitle = false, onDateRangeChange }) => {
  return (
    <div className="table-col">
      {!tableData ? (
        <CircularProgress />
      ) : (
        <>
          <Toolbar sx={{ justifyContent: "center" }}>
            <SelectDateRange value={dateRange as DateRangeValue} onChange={onDateRangeChange} />
          </Toolbar>
          <TableContainer id="table-scroll" className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  {!hideTitle && <TableCell />}
                  {tableData.columns.map((col, index) => (
                    <TableCell key={col.key || index} align="left">
                      {col.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <tbody>
                {tableData.rows.map((row, index) => (
                  <DealTableRow key={row.id || index} hideTitle={hideTitle} row={row} columns={tableData.columns} />
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
};

export const Comparison: React.FC = () => {
  const { getComparisonTableAction, getMetaAction, refetchReportAction, setQueriesAction } = useReportsActions();
  const tableData = useReportsTableSelector();
  const queries = useReportsQueriesSelector();
  const refetchReport = useRefetchReportsSelect();
  const characteristicsAll = useCharacteristics();
  const { support_investor_groups } = useAccountSelector() || {};

  const {
    dateRange,
    secondaryDateRange,
    measures,
    groupBy,
    calculatedFields,
    comparisonColumns,
    rowBy,
    currency,
    filters,
    exchangeMethod,
    use_accounting_date,
    characteristics,
    originalCurrency,
    indexName
  } = queries;

  const { refetch, loading } = useQueryHook(() =>
    getComparisonTableAction({
      dateRange,
      secondaryDateRange,
      measures,
      groupBy,
      calculatedFields,
      comparisonColumns,
      rowBy,
      currency,
      filters,
      exchangeMethod,
      use_accounting_date
    })
  );

  useDidUpdate(() => {
    if (refetchReport === "comparison") {
      refetch();
      refetchReportAction(null);
    }
  }, [refetchReport]);

  useEffect(() => {
    getMetaAction();
  }, []);

  useDidUpdate(() => {
    refetch();
  }, [queries]);

  const groupByOptions: SelectOption[] = useMemo(
    () => [
      { text: "None", key: "None", value: "none" },
      { text: "Investor", key: "Investor", value: "investor" },
      ...(support_investor_groups ? [{ text: "Investor Group", key: "Investor Group", value: "investorGroup" }] : []),
      { text: "Deal", key: "Deal", value: "deal" },
      { text: "Measure", key: "Measure", value: "measure" },
      { text: "Date - month", key: "month", value: "month" },
      { text: "Date - quarter", key: "quarter", value: "quarter" },
      { text: "Date - year", key: "year", value: "year" },
      ...characteristicsAll.map((char) =>
        typeof char === "string" ? { text: char, key: char, value: normalizeGroupByCharacteristic(char) } : char
      )
    ],
    [characteristicsAll, support_investor_groups]
  );

  const tables = useMemo(() => {
    if (tableData) {
      const cols = tableData.columns;
      const mainTable: ReportTableData = { ...tableData, columns: cols.slice(0, cols.length / 2) };
      const secondaryTable: ReportTableData = { ...tableData, columns: cols.slice(cols.length / 2, cols.length) };
      return [mainTable, secondaryTable];
    }
    return [];
  }, [tableData]);

  const handlePrimaryDateRangeChange = (value: DateRangeValue, use_accounting_date?: boolean) => {
    setQueriesAction({ dateRange: value, ...(use_accounting_date !== undefined && { use_accounting_date }) });
  };

  const handleSecondaryDateRangeChange = (value: DateRangeValue, use_accounting_date?: boolean) => {
    setQueriesAction({ secondaryDateRange: value, ...(use_accounting_date !== undefined && { use_accounting_date }) });
  };

  return (
    <div className="comparison">
      <QueryManager showGroupByMenu showColumns showCharacteristics />
      <ReportTableToolbar
        groupByOptions={groupByOptions}
        measures={measures}
        characteristics={characteristics}
        calculatedFields={calculatedFields}
        groupBy={groupBy}
        rowBy={rowBy}
        originalCurrency={originalCurrency}
        indexName={indexName}
        isTableLoading={loading}
      />
      <div className="comparison__content">
        {loading ? (
          <div className="comparison__loading">
            <CircularProgress size={200} />
          </div>
        ) : (
          <>
            <TableCol tableData={tables[0]} dateRange={dateRange} isPrimary={true} onDateRangeChange={handlePrimaryDateRangeChange} />
            <TableCol tableData={tables[1]} dateRange={secondaryDateRange} hideTitle={true} isPrimary={false} onDateRangeChange={handleSecondaryDateRangeChange} />
          </>
        )}
      </div>
    </div>
  );
};
